import React, { FC } from 'react';

import { Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import {
  adminActions,
  adminContractDetails,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import { getHoursDiff } from '../../../Modals/WorkshiftDetails/utils';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { getDayMonthDateYear } from '../../../Reports/helpers';
import { timeString } from '../../../../helpers/Functions';
import { Constants, nameLabels } from '../../../../helpers/Constants';
import strings from '../../../../localization';

import styles from './styles';
import CustomButton from '../../../common/CustomButton';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { setDialog } from '../../../../reducers/dialogReducer';
import LongShiftDistributionModal from '../../../SelfService/PharmacistProfile/LongShiftDistributionModal';

interface IDetailsHeader {
  pharmacyName: string;
  pharmacistName?: string;
  startDate: string;
  endDate: string;
  isCounterOffer?: boolean;
  showViewAllRequests?: boolean;
}

const DetailsHeader: FC<IDetailsHeader> = ({
  pharmacyName,
  pharmacistName,
  startDate,
  endDate,
  showViewAllRequests,
  isCounterOffer,
}) => {
  const dispatch = useAppDispatch();

  const details = useAppSelector(adminContractDetails);
  const { longTermShiftContract, requests } = details ?? {};

  const {
    longTermShiftContractId,
  } = longTermShiftContract ?? {};

  const contractStart = requests?.[0]?.startDate;
  const contractEnd = requests?.slice(-1)[0]?.endDate;

  const range = `${getDayMonthDateYear(contractStart)} to ${getDayMonthDateYear(
    contractEnd,
  )}`;

  const timeformat = useAppSelector(userPreferredTimeFormat);
  const date = getDayMonthDateYear(startDate);
  const time = `${timeString(startDate, endDate, timeformat)} (${getHoursDiff(
    startDate,
    endDate,
  )}${strings.hoursAbbreviation})`;

  const commonFields = [
    { key: '0', label: strings.pharmacyName, value: pharmacyName },
    { key: '1', label: nameLabels().nameLabel, value: pharmacistName },
  ];

  const config = longTermShiftContractId
    ? [...commonFields, { key: '2', label: strings.dateRange, value: range }]
    : [
      ...commonFields,
      { key: '2', label: strings.date, value: date },
      { key: '3', label: strings.time, value: time },
    ];

  const openModal = () => {
    const { pharmacyName: _, ...rest } = details ?? {};
    dispatch(
      setDialog({
        Component: (
          <LongShiftDistributionModal
            successAction={adminActions.adminRequestContractDetails}
            details={{ ...rest, name: rest?.professionalName }}
          />
        ),
        heading: {
          title: `${strings.shiftDetails} (${strings.formatString(
            strings.numberRequests,
            requests?.length,
          )})`,
        },
        showCloseButton: true,
        maxWidth: 'lg',
      }),
    );
  };

  return (
    <Stack sx={styles.container}>
      <Typography sx={styles.title}>
        {isCounterOffer ? strings.counterOfferDetails : strings.details}
      </Typography>
      {config.map((item) => {
        const { key, label, value } = item ?? {};
        return (
          !!value && (
            <Box sx={styles.labelContainer} key={key}>
              <Typography sx={styles.label}>{label}</Typography>
              <Typography sx={styles.value}>{value}</Typography>
            </Box>
          )
        );
      })}

      {showViewAllRequests && (
        <CustomButton
          customClass={styles.footerButton}
          label={strings.viewAllRequests}
          onClick={openModal}
          variant={Constants.variant.outlined}
        />
      )}
    </Stack>
  );
};

export default DetailsHeader;
